<script>
/**
 * @component SidebarNav
 * @description Sidebar de navegación principal colapsable con links agrupados por sección,
 *              control de visibilidad basado en roles, tooltips en modo colapsado
 *              y overlay en mobile. Estado persistido en localStorage.
 * @example <SidebarNav />
 */
import logo from "@/mixins/logo";
import { mapGetters } from "vuex";
import {
  isAppointmentsEnabled,
  isProgramsEnabled,
  isMonitoringEnabled,
  landingUrl,
  theme,
} from "@/services/config";
import api from "@/services/api";
import userService from "@/services/user";
import urgencyProviderService from "@/services/urgency-provider";

const LS_KEY = "sidebar-collapsed";
const LS_SECTIONS_KEY = "sidebar-sections";

/**
 * @description Carga el estado de secciones colapsables desde localStorage.
 *   Consultorio Quantux expandido por defecto, el resto colapsadas.
 * @returns {Object} Estado de cada sección { consultorio: boolean, ... }
 */
function loadSectionState() {
  const defaults = {
    consultorio: true,
    obraSocial: false,
    reportes: true,
    programas: false,
    urgencias: false,
    administracion: false,
    paciente: true,
  };
  try {
    const saved = localStorage.getItem(LS_SECTIONS_KEY);
    if (saved) return { ...defaults, ...JSON.parse(saved) };
  } catch {
    // ignore
  }
  return defaults;
}

export default {
  name: "SidebarNav",
  mixins: [logo],

  data() {
    return {
      isAppointmentsEnabled,
      isProgramsEnabled,
      isMonitoringEnabled,
      theme,
      boxes: [],
      selectedBox: this.$store.state.user.doctor?.selectedBox || null,
      collapsed: localStorage.getItem(LS_KEY) === "true",
      mobileOpen: false,
      windowWidth: window.innerWidth,
      isUrgencyAssigned: false,
      sectionExpanded: loadSectionState(),
    };
  },

  computed: {
    ...mapGetters([
      "isAdmin",
      "isDoctor",
      "isPatient",
      "isAdmissionist",
      "isAssistant",
      "isManager",
      "isSecretary",
      "isHealthAdmin",
      "isPlatformAdmin",
      "hasClinicalAccess",
      "user",
      "role",
      // UC-REG-067 — branding override por org (logo, color). Si la org
      // no tiene branding propio, cae al theme del tenant (mixin logo).
      "orgBranding",
    ]),

    /**
     * @description UC-REG-098 — Indica si el usuario es operador de soporte
     *              (rol `support`). Usado para mostrar la sección "Soporte"
     *              del sidebar. El admin también la ve via isAdminLike.
     * @returns {boolean}
     */
    isSupport() {
      return this.role === "support";
    },

    /** @description Indica si el usuario tiene acceso a la sección administrativa (admin o health-admin) */
    isAdminLike() {
      return this.isAdmin || this.isHealthAdmin;
    },

    /** @returns {string} URL del landing para sacar turno */
    landingUrl() {
      return landingUrl;
    },

    /**
     * @description Si estamos en viewport mobile
     * @returns {boolean}
     */
    isMobile() {
      return this.windowWidth < 768;
    },

    /**
     * @description Si el sidebar está visualmente colapsado (no en mobile)
     * @returns {boolean}
     */
    isCollapsedDesktop() {
      return !this.isMobile && this.collapsed;
    },

    /**
     * @description UC-REG-074 — true si la org tiene AL MENOS UNA feature de
     *              Obra Social encendida. Cuando todas están OFF (caso típico
     *              de un consultorio puro como CNC), ocultamos el header
     *              "Obra Social" entero para no mostrar un dropdown vacío.
     *              Lee del organizationContext con semántica default-ON (si
     *              la org no tiene el override seteado, la feature cuenta
     *              como encendida).
     * @returns {boolean}
     */
    hasAnyOossFeature() {
      const ctx = this.$store.state.organizationContext;
      const features = ctx && ctx.features;
      if (!features) return true; // sin contexto cargado → ON (no esconder)
      const ooss = features.ooss || {};
      const keys = [
        "members",
        "providers",
        "credential",
        "token",
        "validator",
        "authorizations",
        "audit",
        "contracts",
        "billing",
      ];
      // default-ON: clave ausente = encendida.
      return keys.some((k) => ooss[k] !== false);
    },
  },

  watch: {
    $route() {
      this.expandActiveSection();
    },
  },

  created() {
    this.expandActiveSection();
    if (this.isDoctor) {
      api
        .get(`/boxes/user/${this.$store.state.user._id}`)
        .then(({ data }) => {
          this.boxes = data;
        })
        .catch(() => {});

      urgencyProviderService
        .getMyAssignment()
        .then((res) => {
          this.isUrgencyAssigned = !!(
            res &&
            (res.isAssigned || res.specialties?.length)
          );
        })
        .catch(() => {});
    }
  },

  mounted() {
    this._onResize = () => {
      this.windowWidth = window.innerWidth;
    };
    window.addEventListener("resize", this._onResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this._onResize);
  },

  methods: {
    /**
     * @description Verifica si la ruta actual coincide con el nombre dado
     * @param {string} name - Nombre de la ruta
     * @returns {boolean}
     */
    isActive(name) {
      return this.$route.name === name;
    },

    /**
     * @description Verifica si la ruta actual pertenece a un grupo
     * @param {string} baseName - Nombre base del grupo
     * @returns {boolean}
     */
    isInRouteGroup(baseName) {
      const current = this.$route.name || "";
      const byName = current === baseName || current.startsWith(`${baseName}-`);
      const byPath = (this.$route.path || "").startsWith(`/${baseName}`);
      return byName || byPath;
    },

    /**
     * @description Verifica si la ruta actual es alguna de las dadas
     * @param {string[]} routeNames - Array de nombres de rutas
     * @returns {boolean}
     */
    isRouteOneOf(routeNames = []) {
      return routeNames.includes(this.$route.name || "");
    },

    /**
     * @description Devuelve el title del tooltip solo si está colapsado
     * @param {string} label - Texto del tooltip
     * @returns {string|undefined}
     */
    tip(label) {
      return this.isCollapsedDesktop ? label : undefined;
    },

    /**
     * @description Alterna el estado colapsado del sidebar y lo persiste
     */
    toggleCollapse() {
      if (this.isMobile) {
        this.mobileOpen = !this.mobileOpen;
      } else {
        this.collapsed = !this.collapsed;
        localStorage.setItem(LS_KEY, this.collapsed);
        this.$emit("collapse", this.collapsed);
      }
    },

    /**
     * @description Cierra el overlay mobile
     */
    closeMobile() {
      this.mobileOpen = false;
    },

    /**
     * @description Alterna el estado expandido/colapsado de una sección del sidebar
     * @param {string} key - Clave de la sección (principal, obraSocial, administracion, urgencias, paciente)
     */
    toggleSection(key) {
      this.$set(this.sectionExpanded, key, !this.sectionExpanded[key]);
      this.saveSectionState();
    },

    /**
     * @description Persiste el estado de secciones en localStorage
     */
    saveSectionState() {
      localStorage.setItem(
        LS_SECTIONS_KEY,
        JSON.stringify(this.sectionExpanded)
      );
    },

    /**
     * @description Determina qué sección del sidebar contiene la ruta activa
     * @returns {string|null} Clave de la sección activa (null si es Inicio)
     */
    getActiveSection() {
      const name = this.$route.name || "";
      const path = this.$route.path || "";

      if (name === "home") return null;

      if (
        [
          "patient-me",
          "patient-prescription-request",
          "patient-appointments",
          "patient-credential",
          "patient-monitoring",
        ].includes(name)
      )
        return "paciente";

      if (name.startsWith("urgency") || path.startsWith("/urgency"))
        return "urgencias";

      const obraSocialBases = [
        "members",
        "providers",
        "plans",
        "claims",
        "cuenta-medica",
        "service-record",
        "liquidation",
        "contracts",
        "pharmacies",
        "authorization",
        "audit",
      ];
      for (const base of obraSocialBases) {
        if (
          name === base ||
          name.startsWith(`${base}-`) ||
          path.startsWith(`/${base}`)
        )
          return "obraSocial";
      }
      if (name === "doctor-services") return "obraSocial";
      if (name === "settings" && this.$route.query.group === "maestros-os")
        return "obraSocial";

      const programasRoutes = [
        "administration",
        "dashboard-alerts",
        "assistant-evaluation",
        "prescription-queue",
        "prescription-history",
      ];
      if (programasRoutes.includes(name)) return "programas";

      const adminRoutes = [
        "users-list",
        "settings",
        "settings-massive",
        "role-permissions",
        "diagnosis-catalog",
      ];
      if (adminRoutes.includes(name)) return "administracion";
      if (path.startsWith("/user/")) return "administracion";

      return "consultorio";
    },

    /**
     * @description Expande automáticamente la sección que contiene la ruta activa
     */
    expandActiveSection() {
      const section = this.getActiveSection();
      if (section && !this.sectionExpanded[section]) {
        this.$set(this.sectionExpanded, section, true);
        this.saveSectionState();
      }
    },

    /**
     * @description Actualiza el box seleccionado del doctor
     * @param {string} boxId - ID del box
     */
    async setBox(boxId) {
      try {
        await userService.updateSelectedBox(this.$store.state.user._id, boxId);
        this.selectedBox = boxId;
        const updatedUser = {
          ...this.$store.state.user,
          doctor: {
            ...this.$store.state.user.doctor,
            selectedBox: boxId,
          },
        };
        this.$store.commit("setUser", updatedUser);
      } catch (err) {
        console.error("Error setting selected box", err);
      }
    },
  },
};
</script>

<template lang="pug">
div
  //- Overlay mobile
  .sidebar-overlay(v-if="isMobile && mobileOpen" @click="closeMobile")

  nav.sidebar-nav(:class="{ 'is-collapsed': isCollapsedDesktop, 'is-mobile-open': isMobile && mobileOpen, 'is-mobile-hidden': isMobile && !mobileOpen }")
    //- Logo
    //- UC-REG-067 — prioridad: orgBranding.logoUrl > logo del theme tenant >
    //- fallback al icon SVG de quantux. Esto permite que una org dentro del
    //- tenant tenga su propio logo (e.g. policonsultorio con marca propia)
    //- sin afectar a otras orgs del mismo tenant.
    .sidebar-nav__logo
      router-link(:to="{ name: 'home' }")
        //- UC-REG-075: cuando el sidebar esta colapsado y la org tiene
        //- isologo (logoIconUrl) propio, usar el isologo (solo simbolo). Si
        //- no, fallback al logo full y despues a los assets del theme.
        template(v-if="isCollapsedDesktop")
          img(v-if="orgBranding && orgBranding.logoIconUrl" :src="orgBranding.logoIconUrl" alt="Logo")
          img(v-else-if="orgBranding && orgBranding.logoUrl" :src="orgBranding.logoUrl" alt="Logo")
          img(v-else-if="theme === 'vilella' && logoUrl" :src="logoUrl" alt="Logo")
          micon(v-else name="quantux-logo")
        template(v-else)
          img(v-if="orgBranding && orgBranding.logoUrl" :src="orgBranding.logoUrl" alt="Logo")
          img(v-else-if="(theme === 'deragopyan' || theme === 'vilella') && logoAltUrl" :src="logoAltUrl" alt="Logo")
          micon(v-else name="quantux-logo-horizontal-nuevo")

    //- Box selector para doctores
    .sidebar-nav__box-selector(v-if="isDoctor && boxes.length > 0 && !isCollapsedDesktop")
      el-select(
        v-model="selectedBox"
        placeholder="Seleccioná box"
        size="small"
        @change="setBox"
      )
        el-option(label="Sin box" :value="null")
        el-option(
          v-for="box in boxes"
          :key="box._id"
          :label="box.name"
          :value="box._id"
        )

    //- Navegación
    .sidebar-nav__menu
      //- INICIO (siempre visible, fuera de secciones colapsables)
      .sidebar-nav__group
        router-link.sidebar-nav__item(
          :to="{ name: 'home' }"
          exact
          :class="{ 'is-active': isActive('home') }"
          :title="tip('Inicio')"
        )
          micon(name="house")
          span.sidebar-nav__item-text Inicio

      //- PLATAFORMA (solo platform-admin — staff de Quantux). Va arriba de todo
      //- para que vos al loguearte caigas directo al tablero del tenant; admins
      //- de tenants cliente nunca ven este bloque.
      template(v-if="isPlatformAdmin")
        .sidebar-nav__separator
        .sidebar-nav__group
          .sidebar-nav__group-label(v-show="!isCollapsedDesktop")
            span Plataforma
          .sidebar-nav__group-items
            router-link.sidebar-nav__item(
              :to="{ name: 'platform-tenants' }"
              :class="{ 'is-active': isInRouteGroup('platform') }"
              :title="tip('Tenants')"
            )
              micon(name="apartment")
              span.sidebar-nav__item-text Tenants

      //- CONSULTORIO QUANTUX (hidden for patients)
      //- UC-REG-074: cada item gateado con v-has-feature según clave del catalog.
      template(v-if="!isPatient")
        .sidebar-nav__separator
        .sidebar-nav__group
          .sidebar-nav__group-label(v-show="!isCollapsedDesktop" @click="toggleSection('consultorio')")
            span Consultorio Quantux
            micon.sidebar-nav__group-chevron(
              :name="sectionExpanded.consultorio ? 'expand_more' : 'chevron_right'"
            )
          .sidebar-nav__group-items(:class="{ 'is-section-collapsed': !sectionExpanded.consultorio && !isCollapsedDesktop }")
            router-link.sidebar-nav__item(
              v-if="hasClinicalAccess || isSecretary || isAdminLike"
              v-has-feature="'clinic.patients'"
              :to="{ name: 'patient' }"
              :class="{ 'is-active': isInRouteGroup('patient') }"
              :title="tip('Pacientes')"
            )
              micon(name="people")
              span.sidebar-nav__item-text Pacientes

            router-link.sidebar-nav__item(
              v-if="isAppointmentsEnabled && (hasClinicalAccess || isSecretary || isAdminLike || isAssistant)"
              v-has-feature="'clinic.appointments'"
              :to="{ name: 'appointment-calendar' }"
              :class="{ 'is-active': isRouteOneOf(['appointment-calendar', 'appointment-agendas', 'appointment-relocation']) }"
              :title="tip('Agenda')"
            )
              micon(name="calendar")
              span.sidebar-nav__item-text Agenda

            router-link.sidebar-nav__item(
              v-if="isAppointmentsEnabled && isDoctor && hasClinicalAccess"
              v-has-feature="'clinic.attentions'"
              :to="{ name: 'appointment-schedule' }"
              :class="{ 'is-active': isActive('appointment-schedule') }"
              :title="tip('Atenciones')"
            )
              micon(name="list")
              span.sidebar-nav__item-text Atenciones

            router-link.sidebar-nav__item(
              v-has-feature="'ooss.validator'"
              :to="{ name: 'validation' }"
              :class="{ 'is-active': isInRouteGroup('validation') }"
              :title="tip('Validaciones')"
            )
              micon(name="check")
              span.sidebar-nav__item-text Validaciones

            router-link.sidebar-nav__item(
              v-if="hasClinicalAccess || isAdminLike"
              v-has-feature="'clinic.prescriptions'"
              :to="{ name: 'prescription' }"
              :class="{ 'is-active': isInRouteGroup('prescription') }"
              :title="tip('Prescripciones')"
            )
              micon(name="medication")
              span.sidebar-nav__item-text Prescripciones

      //- OBRA SOCIAL (admin and health-admin)
      //- UC-REG-074: cada item gateado individualmente. El grupo+separator se
      //- ocultan solo si TODA la sección OS está apagada (computed hasAnyOossFeature).
      template(v-if="isAdminLike && hasAnyOossFeature")
        .sidebar-nav__separator
        .sidebar-nav__group
          .sidebar-nav__group-label(v-show="!isCollapsedDesktop" @click="toggleSection('obraSocial')")
            span Obra Social
            micon.sidebar-nav__group-chevron(
              :name="sectionExpanded.obraSocial ? 'expand_more' : 'chevron_right'"
            )
          .sidebar-nav__group-items(:class="{ 'is-section-collapsed': !sectionExpanded.obraSocial && !isCollapsedDesktop }")
            router-link.sidebar-nav__item(
              v-has-feature="'ooss.members'"
              :to="{ name: 'members' }"
              :class="{ 'is-active': isInRouteGroup('members') }"
              :title="tip('Afiliados')"
            )
              micon(name="people")
              span.sidebar-nav__item-text Afiliados

            router-link.sidebar-nav__item(
              v-has-feature="'ooss.providers'"
              :to="{ name: 'providers' }"
              :class="{ 'is-active': isInRouteGroup('providers') }"
              :title="tip('Prestadores')"
            )
              micon(name="stethoscope")
              span.sidebar-nav__item-text Prestadores

            router-link.sidebar-nav__item(
              v-has-feature="'ooss.contracts'"
              :to="{ name: 'plans' }"
              :class="{ 'is-active': isInRouteGroup('plans') }"
              :title="tip('Planes')"
            )
              micon(name="description")
              span.sidebar-nav__item-text Planes

            router-link.sidebar-nav__item(
              v-has-feature="'ooss.contracts'"
              :to="{ name: 'contracts' }"
              :class="{ 'is-active': isInRouteGroup('contracts') || isRouteOneOf(['contracts', 'doctor-services']) }"
              :title="tip('Contratos')"
            )
              micon(name="description")
              span.sidebar-nav__item-text Contratos

            router-link.sidebar-nav__item(
              v-has-feature="'ooss.authorizations'"
              :to="{ name: 'authorization' }"
              :class="{ 'is-active': isInRouteGroup('authorization') }"
              :title="tip('Autorizaciones')"
            )
              micon(name="assignment_turned_in")
              span.sidebar-nav__item-text Autorizaciones

            router-link.sidebar-nav__item(
              v-has-feature="'ooss.audit'"
              :to="{ name: 'audit-list' }"
              :class="{ 'is-active': isInRouteGroup('audit') }"
              :title="tip('Auditoría')"
            )
              micon(name="fact_check")
              span.sidebar-nav__item-text Auditoría

            router-link.sidebar-nav__item(
              v-has-feature="'ooss.billing'"
              :to="{ name: 'service-record-list' }"
              :class="{ 'is-active': isInRouteGroup('cuenta-medica') || isInRouteGroup('service-record') || isInRouteGroup('liquidation') }"
              :title="tip('Cuenta Médica')"
            )
              micon(name="money")
              span.sidebar-nav__item-text Cuenta Médica

            router-link.sidebar-nav__item(
              v-has-feature="'ooss.billing'"
              :to="{ name: 'pharmacies' }"
              :class="{ 'is-active': isInRouteGroup('pharmacies') }"
              :title="tip('Farmacias')"
            )
              micon(name="local_pharmacy")
              span.sidebar-nav__item-text Farmacias

            router-link.sidebar-nav__item(
              v-has-feature="'ooss.contracts'"
              :to="{ name: 'settings', query: { group: 'maestros-os' } }"
              :class="{ 'is-active': $route.query.group === 'maestros-os' }"
              :title="tip('Maestros')"
            )
              micon(name="assignment")
              span.sidebar-nav__item-text Maestros

      //- PROGRAMAS DE SALUD
      template(v-if="!isPatient && !isAdmissionist && (hasClinicalAccess || isSecretary || isAdminLike)")
        .sidebar-nav__separator
        .sidebar-nav__group
          .sidebar-nav__group-label(v-show="!isCollapsedDesktop" @click="toggleSection('programas')")
            span Programas de salud
            micon.sidebar-nav__group-chevron(
              :name="sectionExpanded.programas ? 'expand_more' : 'chevron_right'"
            )
          .sidebar-nav__group-items(:class="{ 'is-section-collapsed': !sectionExpanded.programas && !isCollapsedDesktop }")
            router-link.sidebar-nav__item(
              v-if="isProgramsEnabled && isAdminLike"
              :to="{ name: 'administration' }"
              :class="{ 'is-active': isActive('administration') }"
              :title="tip('Programas de salud')"
            )
              micon(name="admin_panel_settings")
              span.sidebar-nav__item-text Programas de salud

            //- UC-REG-094: reverted UC-REG-090. Protocolos no es un programa
            //- en sí mismo sino el configurador de Seguimientos — ahora se
            //- accede por la barra de tabs DENTRO de Seguimientos
            //- (TrackingNav). Quitamos el item para no duplicar accesos.

            router-link.sidebar-nav__item(
              v-if="isMonitoringEnabled"
              :to="{ name: 'dashboard-alerts' }"
              :class="{ 'is-active': isRouteOneOf(['dashboard-alerts', 'assistant-evaluation']) }"
              :title="tip('Seguimientos')"
            )
              micon(name="pending_actions")
              span.sidebar-nav__item-text Seguimientos

            router-link.sidebar-nav__item(
              v-if="isAdminLike"
              :to="{ name: 'prescription-queue' }"
              :class="{ 'is-active': isRouteOneOf(['prescription-queue', 'prescription-history']) }"
              :title="tip('Bandeja de recetas')"
            )
              micon(name="medication")
              span.sidebar-nav__item-text Bandeja de recetas

      //- URGENCIAS (admin, health-admin, or assigned doctor)
      //- v-has-feature gating añadido para que orgs sin guardias (ej. consultorio
      //- programado puro) puedan apagar todo el bloque desde Platform Admin.
      template(v-if="(isAdminLike || isUrgencyAssigned)")
        .sidebar-nav__separator(v-has-feature="'urgency'")
        .sidebar-nav__group(v-has-feature="'urgency'")
          .sidebar-nav__group-label(v-show="!isCollapsedDesktop" @click="toggleSection('urgencias')")
            span Urgencias
            micon.sidebar-nav__group-chevron(
              :name="sectionExpanded.urgencias ? 'expand_more' : 'chevron_right'"
            )
          .sidebar-nav__group-items(:class="{ 'is-section-collapsed': !sectionExpanded.urgencias && !isCollapsedDesktop }")
            router-link.sidebar-nav__item(
              v-if="isAdminLike"
              :to="{ name: 'urgency-config' }"
              :class="{ 'is-active': isActive('urgency-config') }"
              :title="tip('Configuración')"
            )
              micon(name="settings")
              span.sidebar-nav__item-text Configuración

            router-link.sidebar-nav__item(
              v-if="isAdminLike"
              :to="{ name: 'urgency-providers' }"
              :class="{ 'is-active': isActive('urgency-providers') }"
              :title="tip('Prestadores')"
            )
              micon(name="people")
              span.sidebar-nav__item-text Prestadores

            router-link.sidebar-nav__item(
              :to="{ name: 'urgency-queue' }"
              :class="{ 'is-active': isActive('urgency-queue') }"
              :title="tip('Cola de pacientes')"
            )
              micon(name="list")
              span.sidebar-nav__item-text Cola de pacientes

            router-link.sidebar-nav__item(
              v-if="isAdminLike"
              :to="{ name: 'urgency-create-case' }"
              :class="{ 'is-active': isActive('urgency-create-case') }"
              :title="tip('Nuevo caso')"
            )
              micon(name="add_circle_outline")
              span.sidebar-nav__item-text Nuevo caso

      //- ADMINISTRACIÓN (admin and health-admin)
      template(v-if="isAdminLike")
        .sidebar-nav__separator
        .sidebar-nav__group
          .sidebar-nav__group-label(v-show="!isCollapsedDesktop" @click="toggleSection('administracion')")
            span Administración
            micon.sidebar-nav__group-chevron(
              :name="sectionExpanded.administracion ? 'expand_more' : 'chevron_right'"
            )
          .sidebar-nav__group-items(:class="{ 'is-section-collapsed': !sectionExpanded.administracion && !isCollapsedDesktop }")
            router-link.sidebar-nav__item(
              :to="{ name: 'users-list' }"
              :class="{ 'is-active': isInRouteGroup('user') }"
              :title="tip('Usuarios')"
            )
              micon(name="admin_panel_settings")
              span.sidebar-nav__item-text Usuarios

            router-link.sidebar-nav__item(
              :to="{ name: 'role-permissions' }"
              :class="{ 'is-active': isActive('role-permissions') }"
              :title="tip('Roles y permisos')"
            )
              micon(name="admin_panel_settings")
              span.sidebar-nav__item-text Roles y permisos

            router-link.sidebar-nav__item(
              :to="{ name: 'settings' }"
              :class="{ 'is-active': isRouteOneOf(['settings', 'settings-massive']) }"
              :title="tip('Configuración general')"
            )
              micon(name="settings")
              span.sidebar-nav__item-text Configuración general

      //- REPORTES (feature flag — visible solo cuando reports.metabase está
      //- prendida en el tenant, gestionada desde el tablero de Plataforma).
      //- Si no hay feature flag prendida, este bloque queda oculto (la
      //- directiva v-has-feature retira el elemento del DOM). Va al final
      //- del menú (después de ADMINISTRACIÓN) por feedback de Federico —
      //- Reportes es output/análisis, lógicamente cierra el menú.
      template(v-if="!isPatient")
        //- BUG-PV86E3: el separator estaba dentro del .sidebar-nav__group, se
        //- veía más corto que en otras secciones donde está afuera. Ahora va
        //- como hermano del group (consistente con CONSULTORIO, OBRA SOCIAL,
        //- etc.). Mantengo v-has-feature en ambos para que el separator
        //- también desaparezca si la feature flag está apagada.
        .sidebar-nav__separator(v-has-feature="'reports.metabase'")
        .sidebar-nav__group(v-has-feature="'reports.metabase'")
          .sidebar-nav__group-label(v-show="!isCollapsedDesktop" @click="toggleSection('reportes')")
            span Reportes
            micon.sidebar-nav__group-chevron(
              :name="sectionExpanded.reportes ? 'expand_more' : 'chevron_right'"
            )
          .sidebar-nav__group-items(:class="{ 'is-section-collapsed': !sectionExpanded.reportes && !isCollapsedDesktop }")
            router-link.sidebar-nav__item(
              :to="{ name: 'reports-list' }"
              :class="{ 'is-active': isInRouteGroup('reports') }"
              :title="tip('Reportes')"
            )
              micon(name="chart")
              span.sidebar-nav__item-text Reportes

      //- UC-REG-098 — Soporte (operador con rol `support` o admin).
      //- UC-REG-116 — Sección movida fuera del template de Reportes y con su
      //- propio .sidebar-nav__separator para igualar la división visual del
      //- resto de las secciones (Consultorio, Obra Social, etc.). Antes
      //- aparecía pegada a Reportes sin separator entre medio.
      template(v-if="isSupport || isAdmin")
        .sidebar-nav__separator
        .sidebar-nav__group
          .sidebar-nav__group-label(v-show="!isCollapsedDesktop" @click="toggleSection('soporte')")
            span Soporte
            micon.sidebar-nav__group-chevron(
              :name="sectionExpanded.soporte ? 'expand_more' : 'chevron_right'"
            )
          .sidebar-nav__group-items(:class="{ 'is-section-collapsed': !sectionExpanded.soporte && !isCollapsedDesktop }")
            router-link.sidebar-nav__item(
              :to="{ name: 'support-inbox' }"
              :class="{ 'is-active': isInRouteGroup('support-inbox') || isInRouteGroup('support-conversation') }"
              :title="tip('Bandeja')"
            )
              micon(name="email")
              span.sidebar-nav__item-text Bandeja
            router-link.sidebar-nav__item(
              :to="{ name: 'support-kb-list' }"
              :class="{ 'is-active': isInRouteGroup('support-kb') }"
              :title="tip('Base de conocimiento')"
            )
              micon(name="assignment")
              span.sidebar-nav__item-text Base de conocimiento
            router-link.sidebar-nav__item(
              :to="{ name: 'support-metrics' }"
              :class="{ 'is-active': isActive('support-metrics') }"
              :title="tip('Métricas')"
            )
              micon(name="insert_chart_outlined")
              span.sidebar-nav__item-text Métricas
            router-link.sidebar-nav__item(
              v-if="isAdmin"
              :to="{ name: 'support-settings' }"
              :class="{ 'is-active': isActive('support-settings') }"
              :title="tip('Configuración')"
            )
              micon(name="settings")
              span.sidebar-nav__item-text Configuración

      //- PACIENTE
      template(v-if="isPatient")
        .sidebar-nav__separator
        .sidebar-nav__group
          .sidebar-nav__group-label(v-show="!isCollapsedDesktop" @click="toggleSection('paciente')")
            span Mi portal
            micon.sidebar-nav__group-chevron(
              :name="sectionExpanded.paciente ? 'expand_more' : 'chevron_right'"
            )
          .sidebar-nav__group-items(:class="{ 'is-section-collapsed': !sectionExpanded.paciente && !isCollapsedDesktop }")
            router-link.sidebar-nav__item(
              :to="{ name: 'patient-me' }"
              :class="{ 'is-active': isActive('patient-me') }"
              :title="tip('Mi historia clínica')"
            )
              micon(name="assignment")
              span.sidebar-nav__item-text Mi historia clínica

            router-link.sidebar-nav__item(
              v-if="isMonitoringEnabled"
              :to="{ name: 'patient-monitoring' }"
              :class="{ 'is-active': isActive('patient-monitoring') }"
              :title="tip('Mi seguimiento')"
            )
              micon(name="favorite")
              span.sidebar-nav__item-text Mi seguimiento

            router-link.sidebar-nav__item(
              :to="{ name: 'patient-credential' }"
              :class="{ 'is-active': isActive('patient-credential') }"
              :title="tip('Mi credencial')"
            )
              micon(name="id_card")
              span.sidebar-nav__item-text Mi credencial

            router-link.sidebar-nav__item(
              :to="{ name: 'patient-turnos' }"
              :class="{ 'is-active': isActive('patient-turnos') }"
              :title="tip('Turnos')"
            )
              micon(name="perm_contact_calendar")
              span.sidebar-nav__item-text Turnos

            router-link.sidebar-nav__item(
              :to="{ name: 'patient-prescription-request' }"
              :class="{ 'is-active': isActive('patient-prescription-request') }"
              :title="tip('Pedir receta')"
            )
              micon(name="medication")
              span.sidebar-nav__item-text Pedir receta

    //- Toggle button (borde derecho, patrón Figma/Notion)
    .sidebar-nav__toggle(v-if="!isMobile" @click="toggleCollapse")
      micon(:name="isCollapsedDesktop ? 'chevron_right' : 'chevron_left'")
</template>

<style lang="scss" scoped>
.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
}

.sidebar-nav {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 260px;
  background: #fff;
  border-right: 1px solid $border-color;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transition: width 0.3s ease;
  overflow: hidden;

  // ─── Collapsed state ───
  &.is-collapsed {
    width: 64px;

    .sidebar-nav__logo {
      height: 48px;
      padding: 0;
      justify-content: center;

      a {
        justify-content: center;
      }
      .micon {
        width: 24px;
      }
      img {
        width: 24px;
      }
    }

    .sidebar-nav__group {
      padding: 0 8px;
    }

    .sidebar-nav__item {
      justify-content: center;
      padding: 10px 0;
      gap: 0;

      .micon {
        margin: 0;
      }
    }

    .sidebar-nav__item-text {
      display: none;
    }

    .sidebar-nav__group-items {
      max-height: none;
    }

    .sidebar-nav__separator {
      margin: 8px 16px;
    }

    .sidebar-nav__user-main {
      justify-content: center;
      padding: 8px 0;
      margin: 4px 8px;
    }
  }

  // ─── Mobile states ───
  &.is-mobile-hidden {
    transform: translateX(-100%);
  }

  &.is-mobile-open {
    transform: translateX(0);
    width: 260px;
  }

  @media (max-width: 767px) {
    transition: transform 0.3s ease;
  }

  // ─── Subcomponents ───
  &__logo {
    height: 48px;
    padding: 0 20px;
    border-bottom: 1px solid $border-color;
    flex-shrink: 0;
    display: flex;
    align-items: center;

    a {
      text-decoration: none;
      display: flex;
      align-items: center;
    }

    .micon {
      width: 120px;
    }

    img {
      height: auto;
      max-height: 26px;
      width: 120px;
    }
  }

  &__box-selector {
    padding: 12px 16px;
    border-bottom: 1px solid $border-color;
    flex-shrink: 0;

    .el-select {
      width: 100%;
    }
  }

  &__menu {
    flex: 1;
    padding: 12px 0;
    overflow-y: auto;
    overflow-x: hidden;
  }

  &__group {
    padding: 0 12px;
    margin-bottom: 4px;
  }

  &__group-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 600;
    /* Antes: text-transform: uppercase + letter-spacing 0.05em.
       Cambiado a sentence case por feedback de Federico — los headers en
       mayúscula se sentían gritados. Mantengo color muted + weight para que
       sigan funcionando como separador visual entre secciones. */
    color: $text-muted-new;
    padding: 8px 12px 4px;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    border-radius: 6px;
    transition: color 0.15s ease;

    &:hover {
      color: $text-primary-new;

      .sidebar-nav__group-chevron {
        fill: $text-primary-new;
      }
    }
  }

  &__group-chevron {
    width: 14px;
    height: 14px;
    fill: $text-muted-new;
    flex-shrink: 0;
    transition: transform 0.25s ease, fill 0.15s ease;
  }

  &__group-items {
    max-height: 500px;
    overflow: hidden;
    transition: max-height 0.3s ease;

    &.is-section-collapsed {
      max-height: 0;
    }
  }

  &__separator {
    height: 1px;
    background: $border-color;
    margin: 8px 24px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    color: $text-secondary-new;
    text-decoration: none;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.15s ease;
    margin: 2px 0;
    white-space: nowrap;

    .micon {
      fill: $text-secondary-new;
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    &:hover {
      background: #f1f5f9;
      color: $text-primary-new;
      .micon {
        fill: $text-primary-new;
      }
    }

    &.is-active {
      background: $teal-light;
      color: $navy;
      font-weight: 600;
      .micon {
        fill: $navy;
      }
    }
  }

  &__item-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__external-badge {
    font-size: 10px;
    color: $text-muted-new;
    margin-left: auto;
    flex-shrink: 0;
  }

  &__toggle {
    position: absolute;
    top: 50%;
    right: -14px;
    transform: translateY(-50%);
    /* BUG-7Y7UGG (iteración 2): pasamos de 24px a 28px y opacity de 0.6 a 1.
       Fondo blanco se mantiene (probamos cyan en preview y no quedaba bien
       sobre el borde del sidebar). Resultado: botón claramente visible
       siempre sin perder discreción. */
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #fff;
    border: 1px solid $border-color;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 11;
    opacity: 1;
    transition: opacity 0.2s ease, background 0.15s ease;

    .micon {
      width: 12px;
      height: 12px;
      fill: $text-muted-new;
    }

    &:hover {
      background: #f1f5f9;
      .micon {
        fill: $text-primary-new;
      }
    }
  }

  &:hover > &__toggle {
    opacity: 1;
  }

  &.is-collapsed > .sidebar-nav__toggle {
    opacity: 1;
  }
}
</style>
